import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ChevronLeft, Send } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useWedding } from '../../context/WeddingContext'
import CupidIcon from '../../components/CupidIcon'

const CATEGORY_LABELS = {
  photographer:'Photographer', videographer:'Videographer', decorator:'Decorator',
  mc_dj:'MC / DJ', caterer:'Caterer', aso_ebi:'Aso Ebi', makeup:'Makeup Artist',
  bridal_fashion:'Bridal Fashion', venue:'Venue', live_band:'Live Band',
  cake:'Cake Designer', planner:'Event Planner',
}
const fmtPrice = (n, cur='NGN') => n ? `${cur==='NGN'?'₦':cur+' '}${Number(n).toLocaleString()}` : null

const OPENING = "Tell me about the day you're dreaming of — the vibe, the style, anything that matters to you. I'll find vendors who fit."

function Bubble({ from, children }) {
  const mine = from === 'me'
  return (
    <div style={{ display:'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
      <div style={{ maxWidth:'80%', background: mine ? '#C4826A' : '#fff', color: mine ? '#fff' : '#1A1A1A',
        border: mine ? 'none' : '1px solid #E5E0D8', borderRadius: mine ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
        padding:'0.7rem 0.95rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.9rem', lineHeight:1.55 }}>
        {children}
      </div>
    </div>
  )
}

function MatchCard({ m }) {
  const navigate = useNavigate()
  const price = fmtPrice(m.starting_price, m.price_currency)
  return (
    <div className="card" style={{ display:'flex', gap:'0.875rem', padding:'0.875rem', alignItems:'center' }}>
      <img src={m.profile_image_url} alt={m.business_name}
        style={{ width:64, height:64, borderRadius:'0.75rem', objectFit:'cover', flexShrink:0, background:'#F3F0EA' }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
          <h4 style={{ fontFamily:'Gibs, serif', fontWeight:600, fontSize:'1rem', margin:0 }}>{m.business_name}</h4>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.66rem', fontWeight:700, color:'#fff', background:'#9BAF9B', borderRadius:100, padding:'1px 8px' }}>{m.match_percent}% match</span>
        </div>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0.1rem 0 0.25rem' }}>
          {CATEGORY_LABELS[m.category] || m.category}{m.city ? ` · ${m.city}` : ''}{price ? ` · from ${price}` : ''}
        </p>
        {m.reason && <p style={{ fontFamily:'Gibs, serif', fontStyle:'italic', fontSize:'0.8rem', color:'#5C4033', margin:'0 0 0.5rem', lineHeight:1.5 }}>{m.reason}</p>}
        <button onClick={() => navigate(`/dashboard/aisle/vendor/${m.vendor_id}`)}
          style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.74rem', color:'#C4826A', background:'#fff', border:'1.5px solid #E5E0D8', borderRadius:100, padding:'0.35rem 0.9rem', cursor:'pointer' }}>
          View Profile
        </button>
      </div>
    </div>
  )
}

export default function CupidPage() {
  const navigate = useNavigate()
  const { wedding } = useWedding()
  const [msgs, setMsgs] = useState([{ from:'cupid', text: OPENING }])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const endRef = useRef(null)

  useEffect(() => { endRef.current?.scrollIntoView({ behavior:'smooth' }) }, [msgs, busy])

  async function send() {
    const text = input.trim()
    if (!text || busy) return
    setInput('')
    setMsgs(m => [...m, { from:'me', text }])
    setBusy(true)
    try {
      const { data, error } = await supabase.functions.invoke('aisle-cupid', {
        body: { message: text, city: wedding?.city || '', date: wedding?.wedding_date || '' },
      })
      if (error) throw error
      const matches = data?.matches || []
      if (matches.length === 0) {
        setMsgs(m => [...m, { from:'cupid', text:'fallback' }])
      } else {
        setMsgs(m => [...m, { from:'cupid', text:'Here are the vendors I think you’ll love:', matches }])
      }
    } catch {
      setMsgs(m => [...m, { from:'cupid', text:'fallback' }])
    }
    setBusy(false)
  }

  return (
    <div style={{ maxWidth:600, margin:'0 auto' }}>
      <button onClick={() => navigate('/dashboard/aisle')}
        style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'none', border:'none', cursor:'pointer',
          fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', marginBottom:'1rem', padding:0 }}>
        <ChevronLeft size={16} /> The Aisle
      </button>

      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'1.25rem' }}>
        <div style={{ width:40, height:40, borderRadius:'50%', background:'rgba(196,130,106,0.12)', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <CupidIcon size={28} variant="gradient" />
        </div>
        <h1 style={{ fontFamily:'Gibs, serif', fontWeight:600, fontSize:'1.4rem', margin:0 }}>Cupid</h1>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem', marginBottom:'1rem' }}>
        {msgs.map((m, i) => (
          m.text === 'fallback' ? (
            <Bubble key={i} from="cupid">
              I couldn't find a strong match just yet — try{' '}
              <Link to="/dashboard/aisle" style={{ color:'#C4826A', fontWeight:700 }}>browsing by category</Link> instead.
            </Bubble>
          ) : (
            <div key={i} style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
              <Bubble from={m.from}>{m.text}</Bubble>
              {m.matches && m.matches.map(mm => <MatchCard key={mm.vendor_id} m={mm} />)}
            </div>
          )
        ))}
        {busy && <Bubble from="cupid"><span style={{ color:'#9B9B9B' }}>Cupid is looking…</span></Bubble>}
        <div ref={endRef} />
      </div>

      <div style={{ display:'flex', gap:'0.5rem', position:'sticky', bottom:0, background:'#FAFAF8', paddingTop:'0.5rem' }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') send() }}
          placeholder="e.g. a relaxed garden wedding, warm earthy colours…"
          style={{ flex:1, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.75rem 1.1rem', outline:'none' }} />
        <button onClick={send} disabled={busy || !input.trim()}
          style={{ width:46, height:46, flexShrink:0, borderRadius:'50%', border:'none', cursor: busy ? 'default' : 'pointer',
            background: busy || !input.trim() ? '#D4C4BC' : '#C4826A', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Send size={18} />
        </button>
      </div>
    </div>
  )
}
